/*
 * Vagustim product landing page behaviour.
 *
 * - native draggable / keyboard / arrow card sliders
 * - video lightbox (YouTube / Vimeo / Shopify-hosted)
 * - infinite logo marquee
 * - image tabs
 * - reveal toggles and single-open accordions
 *
 * Initialisers are idempotent and safe with Shopify Theme Editor section
 * re-renders. The global guard prevents this asset from registering duplicate
 * document/window listeners when more than one section includes the asset.
 */
(function () {
  'use strict';

  if (window.__vagustimPdpLoaded) {
    if (typeof window.vagustimPdpInit === 'function') {
      window.vagustimPdpInit(document);
    }
    return;
  }
  window.__vagustimPdpLoaded = true;

  var REDUCED = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function slidesOf(track) {
    var items = Array.prototype.slice.call(track.querySelectorAll('[data-vg-slide]'));
    if (!items.length) items = Array.prototype.slice.call(track.children);
    return items;
  }

  /* ==================================================================== */
  /*  Slider                                                              */
  /* ==================================================================== */
  function initCarousel(root) {
    var track = root.querySelector('[data-vg-track]');
    if (!track || track.dataset.vgCarousel === 'true') return;

    var dotsWrap = root.querySelector('[data-vg-dots]');
    var prev = root.querySelector('[data-vg-prev]');
    var next = root.querySelector('[data-vg-next]');
    var items = slidesOf(track);
    if (!items.length) return;

    track.dataset.vgCarousel = 'true';

    var autoplayMs = parseInt(root.getAttribute('data-vg-autoplay'), 10) || 0;
    var timer = null;
    var raf = 0;
    var destroyed = false;

    function gap() {
      var cs = getComputedStyle(track);
      return parseFloat(cs.columnGap || cs.gap || 0) || 0;
    }

    function step() {
      if (!items[0]) return 0;
      return items[0].getBoundingClientRect().width + gap();
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function pageCount() {
      var distance = step();
      if (!distance || maxScroll() <= 1) return 1;
      return Math.max(1, Math.round(maxScroll() / distance) + 1);
    }

    function pageLeft(index) {
      return clamp(index * step(), 0, maxScroll());
    }

    function activePage() {
      var distance = step();
      if (!distance || maxScroll() <= 1) return 0;
      if (track.scrollLeft >= maxScroll() - 2) return pageCount() - 1;
      return clamp(Math.round(track.scrollLeft / distance), 0, pageCount() - 1);
    }

    function scrollToLeft(left, smooth) {
      track.scrollTo({
        left: clamp(left, 0, maxScroll()),
        behavior: smooth && !REDUCED ? 'smooth' : 'auto'
      });
    }

    function goToPage(index, smooth) {
      scrollToLeft(pageLeft(clamp(index, 0, pageCount() - 1)), smooth !== false);
    }

    function go(dir, wrap) {
      var current = activePage();
      var last = pageCount() - 1;
      var target = current + dir;

      if (wrap) {
        if (target > last) target = 0;
        if (target < 0) target = last;
      } else {
        target = clamp(target, 0, last);
      }

      goToPage(target, true);
    }

    /* ---- dots ---- */
    function syncDots() {
      if (!dotsWrap) return;
      var index = activePage();
      Array.prototype.forEach.call(dotsWrap.children, function (dot, i) {
        var active = i === index;
        dot.classList.toggle('vg-is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function buildDots() {
      if (!dotsWrap) return;
      var count = pageCount();

      if (dotsWrap.children.length === count) {
        syncDots();
        return;
      }

      dotsWrap.innerHTML = '';
      if (count < 2) return;

      for (var i = 0; i < count; i++) {
        (function (index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', 'Go to review position ' + (index + 1) + ' of ' + count);
          dot.addEventListener('click', function () {
            stopAuto();
            goToPage(index, true);
          });
          dotsWrap.appendChild(dot);
        })(i);
      }

      syncDots();
    }

    /* ---- arrows ---- */
    function syncArrows() {
      var overflow = maxScroll() > 1;
      var atStart = !overflow || track.scrollLeft <= 1;
      var atEnd = !overflow || track.scrollLeft >= maxScroll() - 1;

      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
      root.classList.toggle('vg-at-start', atStart);
      root.classList.toggle('vg-at-end', atEnd);
      root.classList.toggle('vg-no-overflow', !overflow);
    }

    if (prev) {
      prev.addEventListener('click', function () {
        stopAuto();
        go(-1, false);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        stopAuto();
        go(1, false);
      });
    }

    /* ---- keyboard ---- */
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        stopAuto();
        go(1, false);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        stopAuto();
        go(-1, false);
      } else if (e.key === 'Home') {
        e.preventDefault();
        stopAuto();
        goToPage(0, true);
      } else if (e.key === 'End') {
        e.preventDefault();
        stopAuto();
        goToPage(pageCount() - 1, true);
      }
    });

    /* ---- smooth pointer dragging + momentum ---- */
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;
    var lastX = 0;
    var lastMoveTime = 0;
    var velocity = 0;
    var dragTarget = 0;
    var dragRaf = 0;
    var lastDragEnd = 0;

    function renderDrag() {
      dragRaf = 0;
      if (!dragging || !moved) return;
      track.scrollLeft = dragTarget;
    }

    function queueDragRender() {
      if (dragRaf) return;
      dragRaf = window.requestAnimationFrame(renderDrag);
    }

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('input, textarea, select')) return;

      dragging = true;
      moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      lastX = e.clientX;
      lastMoveTime = performance.now();
      velocity = 0;
      dragTarget = startScroll;
      track.classList.add('vg-is-grabbing');
    });

    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;

      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < 4) return;

      if (!moved) {
        moved = true;
        track.style.scrollSnapType = 'none';
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
        stopAuto();
      }

      if (e.cancelable) e.preventDefault();

      var now = performance.now();
      var elapsed = Math.max(1, now - lastMoveTime);
      var pointerDelta = e.clientX - lastX;
      var instantVelocity = -pointerDelta / elapsed;

      /* Smooth noisy pointer samples without making the drag feel delayed. */
      velocity = velocity * 0.72 + instantVelocity * 0.28;
      lastX = e.clientX;
      lastMoveTime = now;

      dragTarget = clamp(startScroll - dx, 0, maxScroll());
      queueDragRender();
    }, { passive: false });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('vg-is-grabbing');

      if (dragRaf) {
        window.cancelAnimationFrame(dragRaf);
        dragRaf = 0;
        if (moved) track.scrollLeft = dragTarget;
      }

      if (!moved) return;

      lastDragEnd = Date.now();

      if (e && e.pointerId != null) {
        try { track.releasePointerCapture(e.pointerId); } catch (err) {}
      }

      /* Project the release velocity forward, then settle on the nearest card.
         The clamp prevents a fast flick from jumping an excessive distance. */
      var distance = step();
      var momentum = clamp(velocity * 220, -distance * 1.35, distance * 1.35);
      var projectedLeft = clamp(track.scrollLeft + momentum, 0, maxScroll());
      var targetIndex = distance ? Math.round(projectedLeft / distance) : 0;
      var targetLeft = pageLeft(targetIndex);

      /* Restore snapping only after starting the controlled settle animation. */
      track.scrollTo({
        left: targetLeft,
        behavior: REDUCED ? 'auto' : 'smooth'
      });

      window.setTimeout(function () {
        if (!destroyed) track.style.scrollSnapType = '';
      }, REDUCED ? 0 : 320);
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener('click', function (e) {
      if (Date.now() - lastDragEnd < 300) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    track.addEventListener('dragstart', function (e) {
      if (e.target.tagName === 'IMG') e.preventDefault();
    });

    /* ---- autoplay ---- */
    function startAuto() {
      if (destroyed || !autoplayMs || REDUCED || timer || document.hidden || pageCount() < 2) return;
      timer = window.setInterval(function () {
        go(1, true);
      }, autoplayMs);
    }

    function stopAuto() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }

    function onVisibilityChange() {
      if (document.hidden) stopAuto();
      else if (!root.matches(':hover') && !root.contains(document.activeElement)) startAuto();
    }

    if (autoplayMs && !REDUCED) {
      root.addEventListener('mouseenter', stopAuto);
      root.addEventListener('mouseleave', startAuto);
      root.addEventListener('focusin', stopAuto);
      root.addEventListener('focusout', function () {
        window.setTimeout(function () {
          if (!root.contains(document.activeElement) && !root.matches(':hover')) startAuto();
        }, 0);
      });
      document.addEventListener('visibilitychange', onVisibilityChange);
      startAuto();
    }

    function onScroll() {
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(function () {
        syncDots();
        syncArrows();
      });
    }

    track.addEventListener('scroll', onScroll, { passive: true });

    function onResize() {
      window.clearTimeout(track._vgResize);
      track._vgResize = window.setTimeout(function () {
        if (destroyed) return;
        buildDots();
        syncArrows();
        goToPage(activePage(), false);
      }, 150);
    }

    window.addEventListener('resize', onResize);

    track._vgCarouselGoTo = function (index, smooth) {
      stopAuto();
      goToPage(index, smooth !== false);
    };
    track._vgCarouselStop = stopAuto;
    track._vgCarouselDestroy = function () {
      if (destroyed) return;
      destroyed = true;
      stopAuto();
      window.cancelAnimationFrame(raf);
      window.clearTimeout(track._vgResize);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      delete track._vgCarouselGoTo;
      delete track._vgCarouselStop;
      delete track._vgCarouselDestroy;
      delete track.dataset.vgCarousel;
    };

    buildDots();
    syncArrows();
  }

  function destroyCarousels(scope) {
    var context = scope || document;
    context.querySelectorAll('[data-vg-track]').forEach(function (track) {
      if (typeof track._vgCarouselDestroy === 'function') track._vgCarouselDestroy();
    });
  }

  /* ==================================================================== */
  /*  Video lightbox                                                      */
  /* ==================================================================== */
  var modal = null;
  var modalCloseTimer = null;
  var lastFocused = null;
  var MODAL_TRANSITION_MS = 220;

  function buildModal() {
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'vg-scope vg-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Video');
    modal.hidden = true;
    modal.innerHTML =
      '<div class="vg-modal__backdrop" data-vg-modal-close></div>' +
      '<div class="vg-modal__dialog" role="document">' +
      '<div class="vg-modal__frame">' +
      '<button class="vg-modal__close" type="button" data-vg-modal-close aria-label="Close video">' +
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<div class="vg-modal__media" data-vg-modal-media></div>' +
      '</div>' +
      '</div>';

    document.body.appendChild(modal);

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-vg-modal-close]')) closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (!modal || modal.hidden) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
      } else if (e.key === 'Tab') {
        trapFocus(e);
      }
    });

    return modal;
  }

  function trapFocus(e) {
    if (!modal) return;

    var focusables = Array.prototype.slice.call(
      modal.querySelectorAll('button:not([disabled]), [href], iframe, video, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) {
      return !el.hidden && el.getAttribute('aria-hidden') !== 'true';
    });

    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function stopModalPlayback(media) {
    if (!media) return;

    /* Stop native Shopify-hosted media immediately. */
    media.querySelectorAll('video, audio').forEach(function (player) {
      try {
        player.pause();
        player.currentTime = 0;
      } catch (err) {}
    });

    /* Removing an iframe stops YouTube/Vimeo. Blank the source first as an
       extra safeguard so playback cannot continue during the close animation. */
    media.querySelectorAll('iframe').forEach(function (iframe) {
      try { iframe.src = 'about:blank'; } catch (err) {}
    });

    media.innerHTML = '';
  }

  function finishModalClose() {
    if (!modal) return;
    modal.hidden = true;
    modal.classList.remove('vg-modal--open');
    modalCloseTimer = null;
  }

  function openModal(node, label) {
    buildModal();

    if (modalCloseTimer) {
      window.clearTimeout(modalCloseTimer);
      modalCloseTimer = null;
    }

    var media = modal.querySelector('[data-vg-modal-media]');
    stopModalPlayback(media);
    media.appendChild(node);

    lastFocused = document.activeElement;
    modal.setAttribute('aria-label', label || 'Video');
    modal.hidden = false;
    document.documentElement.classList.add('vg-modal-open');

    /* Add the open state on the next paint so opacity/transform transitions
       run reliably even when the modal has just changed from [hidden]. */
    window.requestAnimationFrame(function () {
      if (!modal || modal.hidden) return;
      modal.classList.add('vg-modal--open');
    });

    var closeBtn = modal.querySelector('.vg-modal__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });

    var video = media.querySelector('video');
    if (video && typeof video.play === 'function') {
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
    }
  }

  function closeModal() {
    if (!modal || modal.hidden) return;

    /* Playback is stopped before the visual close animation begins. */
    stopModalPlayback(modal.querySelector('[data-vg-modal-media]'));
    modal.classList.remove('vg-modal--open');
    document.documentElement.classList.remove('vg-modal-open');

    if (modalCloseTimer) window.clearTimeout(modalCloseTimer);

    if (REDUCED) {
      finishModalClose();
    } else {
      modalCloseTimer = window.setTimeout(finishModalClose, MODAL_TRANSITION_MS);
    }

    if (lastFocused && document.documentElement.contains(lastFocused) && lastFocused.focus) {
      try { lastFocused.focus({ preventScroll: true }); }
      catch (err) { lastFocused.focus(); }
    }
    lastFocused = null;
  }

  function embedFor(trigger) {
    var type = trigger.getAttribute('data-vg-video-type');
    var id = trigger.getAttribute('data-vg-video-id');

    if (type === 'hosted') {
      var card = trigger.closest('[data-vg-slide]') || trigger.parentNode;
      var tpl = card && card.querySelector('template[data-vg-video-template]');
      if (!tpl) return null;

      var frag = tpl.content.cloneNode(true);
      var video = frag.querySelector('video');
      if (video) {
        video.setAttribute('controls', 'controls');
        video.setAttribute('autoplay', 'autoplay');
        video.setAttribute('playsinline', 'playsinline');
        video.setAttribute('preload', 'metadata');
      }
      return frag;
    }

    var src = null;
    if (type === 'youtube' && id) {
      src = 'https://www.youtube.com/embed/' + encodeURIComponent(id) +
        '?autoplay=1&rel=0&playsinline=1';
    } else if (type === 'vimeo' && id) {
      src = 'https://player.vimeo.com/video/' + encodeURIComponent(id) +
        '?autoplay=1&playsinline=1';
    }

    if (!src) return null;

    var iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'eager');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.setAttribute('title', trigger.getAttribute('aria-label') || 'Video');
    return iframe;
  }

  function initVideoTriggers(scope) {
    scope.querySelectorAll('[data-vg-video-type]').forEach(function (trigger) {
      if (trigger.dataset.vgVideoInit === 'true') return;
      trigger.dataset.vgVideoInit = 'true';

      trigger.addEventListener('click', function (e) {
        var node = embedFor(trigger);
        if (!node) return;

        e.preventDefault();
        e.stopPropagation();
        openModal(node, trigger.getAttribute('aria-label') || 'Video');
      });
    });
  }

  /* ==================================================================== */
  /*  Logo marquee                                                        */
  /* ==================================================================== */
  function initMarquee(root) {
    if (root.dataset.vgMarqueeInit === 'true') return;
    root.dataset.vgMarqueeInit = 'true';

    var track = root.querySelector('[data-vg-marquee-track]');
    var group = root.querySelector('[data-vg-marquee-group]');
    if (!track || !group) return;

    var speed = parseFloat(root.getAttribute('data-vg-speed')) || 40;

    function layout() {
      Array.prototype.slice.call(track.querySelectorAll('[data-vg-marquee-clone]')).forEach(function (node) {
        node.parentNode.removeChild(node);
      });

      var groupWidth = group.scrollWidth;
      if (!groupWidth) return;

      var needed = Math.max(2, Math.ceil((root.clientWidth * 2) / groupWidth) + 1);
      for (var i = 1; i < needed; i++) {
        var clone = group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-vg-marquee-clone', '');
        clone.removeAttribute('data-vg-marquee-group');
        track.appendChild(clone);
      }

      track.style.setProperty('--vg-marquee-distance', groupWidth + 'px');
      track.style.setProperty('--vg-marquee-duration', (groupWidth / speed).toFixed(2) + 's');
      root.classList.add('vg-marquee--ready');
    }

    layout();

    window.addEventListener('resize', function () {
      window.clearTimeout(root._vgMarqueeResize);
      root._vgMarqueeResize = window.setTimeout(layout, 200);
    });

    root.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', function () {
        window.clearTimeout(root._vgMarqueeResize);
        root._vgMarqueeResize = window.setTimeout(layout, 50);
      });
    });
  }

  /* ==================================================================== */
  /*  Step tabs                                                           */
  /* ==================================================================== */
  function initTabs(root) {
    if (root.dataset.vgTabsInit === 'true') return;
    root.dataset.vgTabsInit = 'true';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-vg-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-vg-tab-panel]'));
    if (tabs.length < 2) return;

    function select(index, focus) {
      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.classList.toggle('vg-how__step--active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });

      panels.forEach(function (panel, i) {
        var on = i === index;
        panel.classList.toggle('vg-is-active', on);
        panel.hidden = !on;
      });

      if (focus && tabs[index]) tabs[index].focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i); });
      tab.addEventListener('keydown', function (e) {
        var last = tabs.length - 1;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          select(i);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          select(i === last ? 0 : i + 1, true);
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          select(i === 0 ? last : i - 1, true);
        } else if (e.key === 'Home') {
          e.preventDefault();
          select(0, true);
        } else if (e.key === 'End') {
          e.preventDefault();
          select(last, true);
        }
      });
    });

    var initial = tabs.findIndex(function (tab) {
      return tab.getAttribute('aria-selected') === 'true';
    });
    select(initial < 0 ? 0 : initial);
  }

  /* ==================================================================== */
  /*  Reveal toggle                                                       */
  /* ==================================================================== */
  function initToggle(root) {
    var btn = root.querySelector('[data-vg-toggle]');
    if (!btn || btn.dataset.vgToggleInit === 'true') return;
    btn.dataset.vgToggleInit = 'true';

    var target = root.querySelector('[data-vg-toggle-target]') || root;
    var moreLabel = btn.dataset.labelMore || btn.textContent.trim();
    var lessLabel = btn.dataset.labelLess || 'Show less';
    var span = btn.querySelector('span') || btn;

    btn.addEventListener('click', function () {
      var expanded = target.classList.toggle('vg-is-expanded');
      span.textContent = expanded ? lessLabel : moreLabel;
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  /* ==================================================================== */
  /*  Accordion                                                           */
  /* ==================================================================== */
 /* ==================================================================== */
/*  Accordion                                                           */
/* ==================================================================== */
function initAccordion(root) {
  if (root.dataset.vgAccordionInit === 'true') return;
  root.dataset.vgAccordionInit = 'true';

  var single = root.dataset.vgSingle !== 'false';
  var buttons = Array.prototype.slice.call(
    root.querySelectorAll('[data-vg-acc-btn]')
  );
  var items = Array.prototype.slice.call(
    root.querySelectorAll('[data-vg-acc-item]')
  );

  buttons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var item = btn.closest('[data-vg-acc-item]');
      var panel = document.getElementById(
        btn.getAttribute('aria-controls')
      );

      var open = btn.getAttribute('aria-expanded') === 'true';
      var willOpen = !open;

      /*
       * Close every other accordion item when
       * data-vg-single="true".
       */
      if (single && willOpen) {
        buttons.forEach(function (other) {
          if (other === btn) return;

          other.setAttribute('aria-expanded', 'false');

          var otherPanel = document.getElementById(
            other.getAttribute('aria-controls')
          );

          if (otherPanel) {
            otherPanel.classList.remove('vg-is-open');
          }

          var otherItem = other.closest('[data-vg-acc-item]');

          if (otherItem) {
            /*
             * Remove BOTH active classes.
             * This prevents the initially selected accordion
             * from keeping its active color.
             */
            otherItem.classList.remove(
              'vg-is-open',
              'vg-feat__row--open'
            );
          }
        });
      }

      /*
       * Update clicked accordion.
       */
      btn.setAttribute(
        'aria-expanded',
        willOpen ? 'true' : 'false'
      );

      if (panel) {
        panel.classList.toggle('vg-is-open', willOpen);
      }

      if (item) {
        item.classList.toggle('vg-is-open', willOpen);
        item.classList.toggle('vg-feat__row--open', willOpen);
      }
    });
  });

  /*
   * Make the entire bordered accordion row clickable.
   *
   * We forward the row click to the existing accordion button instead of
   * duplicating the toggle logic. That keeps aria-expanded, single-open
   * behaviour, and any other button click listeners perfectly in sync.
   */
  items.forEach(function (item) {
    item.addEventListener('click', function (event) {
      /*
       * A direct click on the real accordion button already runs the
       * correct handler. Do not forward it again or it would toggle twice.
       */
      if (event.target.closest('[data-vg-acc-btn]')) return;

      /*
       * Future-proof the row in case links/form controls are added later.
       * Those interactive elements should keep their own native behaviour.
       */
      if (
        event.target.closest(
          'a, input, textarea, select, option, label, [contenteditable="true"], [data-vg-ignore-row-click]'
        )
      ) {
        return;
      }

      var btn = item.querySelector('[data-vg-acc-btn]');
      if (!btn || btn.disabled) return;

      btn.click();
    });
  });
}


  function initAll(scope) {
    var context = scope || document;
    context.querySelectorAll('[data-vg-carousel]').forEach(initCarousel);
    context.querySelectorAll('[data-vg-marquee]').forEach(initMarquee);
    context.querySelectorAll('[data-vg-tabs]').forEach(initTabs);
    context.querySelectorAll('[data-vg-toggle-root]').forEach(initToggle);
    context.querySelectorAll('[data-vg-acc]').forEach(initAccordion);
    initVideoTriggers(context);
  }

  ready(function () { initAll(document); });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    destroyCarousels(e.target);
    closeModal();
  });

  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target.matches && e.target.matches('[data-vg-slide]')
      ? e.target
      : (e.target.querySelector ? e.target.querySelector('[data-vg-slide]') : null);

    if (!slide) return;

    var track = slide.closest('[data-vg-track]');
    if (!track || typeof track._vgCarouselGoTo !== 'function') return;

    var items = slidesOf(track);
    var index = items.indexOf(slide);
    if (index >= 0) track._vgCarouselGoTo(index, true);
  });

  window.vagustimPdpInit = initAll;
})();
